import { useState } from 'react';

import { Icon } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import useCurrency, { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';

interface FeeConfigAccordionProps {
  className?: string;
  feeConfig: {
    category: string;
    amount: string;
  }[];
  totalAmount?: string;
  fineAmount?: string;
  subTotalAmount?: string;
  closedAmount?: string;
  gstInfo?: {
    cgstPercent?: string;
    sgstPercent?: string;
    igstPercent?: string;
    cgstAmount?: string;
    sgstAmount?: string;
    igstAmount?: string;
    totalGst?: string;
  };
  discountAmount?: string;
  expandable?: boolean;
  /** V2 analytics only - fires when the accordion is expanded (not collapsed). */
  onExpand?: () => void;
}

const gstLineLabel = (name: string, percent?: string) => {
  const p = percent?.trim();
  return p ? `${name} (${p}%)` : name;
};

const TotalAmountFooter = ({
  isDefaultCurrency,
  totalAmount,
  openAccordion,
  showChevron
}: {
  isDefaultCurrency: boolean;
  totalAmount?: string;
  openAccordion: boolean;
  showChevron: boolean;
}) => (
  <div className="flex w-full min-w-0 items-center justify-between gap-3">
    <div className="flex min-w-0 items-center gap-2">
      <div className="text-sm font-medium text-primary">Total Amount</div>
      {showChevron ? (
        <Icon
          name="down-icon-green"
          size="extrasmall"
          className={openAccordion ? 'rotate-180' : ''}
        />
      ) : null}
    </div>
    <div className="flex shrink-0 items-center justify-end">
      <CurrencyAmount
        value={Number(totalAmount)}
        className={isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}
        symbolClass={isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''}
        valueClass=""
      />
    </div>
  </div>
);

const FeeConfigAccordion = ({
  className,
  feeConfig,
  totalAmount,
  fineAmount,
  subTotalAmount,
  closedAmount,
  gstInfo,
  discountAmount,
  expandable = true,
  onExpand
}: FeeConfigAccordionProps) => {
  const [openAccordion, setOpenAccordion] = useState<boolean>(false);
  const showExpanded = expandable && openAccordion;
  const { currencyConfig } = useCurrency();

  const handleToggleExpand = () => {
    if (!openAccordion) onExpand?.();
    setOpenAccordion((open) => !open);
  };

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const hasVisibleFeeBreakdown = feeConfig.some(
    (item) => Number(item.amount) !== 0
  );

  // const cgstpercent = 20; // convert to a prop having value from api
  // const sgstpercent = 20; // convert to a prop having value from api
  // const cgstAmount = 20; // convert to a prop having value from api
  // const sgstAmount = 20; // convert to a prop having value from api
  // const totalGstAmount = 20; // convert to a prop having value from api
  // const iscgst = true; // convert to a prop having value from api
  // const issgst = true; // convert to a prop having value from api
  return (
    <div
      className={`overflow-hidden rounded-xl border border-grey40 py-3 ${
        className || ''
      }`}
    >
      {/* eslint-disable-next-line no-nested-ternary */}
      {showExpanded ? (
        <>
          <div className="-mt-3 rounded-t-xl bg-cofeeGreyBlue1 px-4 py-3">
            {feeConfig.map(
              (item, index) =>
                Number(item.amount) !== 0 && (
                  <div
                    className={`${
                      index !== 0 ? 'mt-3' : ''
                    } flex justify-between`}
                    key={item.category}
                  >
                    <div className="text-sm font-normal text-grey">
                      {item.category}
                    </div>
                    <div className="flex items-center justify-center text-sm font-normal text-content">
                      <CurrencyAmount
                        value={Number(item.amount)}
                        className={`${
                          isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                        }`}
                        symbolClass={`${
                          isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                        }`}
                        valueClass=""
                      />
                    </div>
                  </div>
                )
            )}
            <div
              className={`flex cursor-pointer items-center justify-between py-3 ${
                hasVisibleFeeBreakdown
                  ? 'mt-3 border-t border-cofeeGreyBlue5'
                  : ''
              }`}
            >
              <div className="text-sm font-normal text-grey">Sub Total</div>
              <div className="flex items-center justify-center text-sm font-normal text-content">
                <CurrencyAmount
                  value={Number(subTotalAmount)}
                  className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                  symbolClass={`${
                    isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                  }`}
                  valueClass=""
                />
              </div>
            </div>
            {fineAmount && parseFloat(fineAmount) > 0 && (
              <div
                className={`flex cursor-pointer justify-between py-3 ${
                  hasVisibleFeeBreakdown ? 'border-t border-cofeeGreyBlue5' : ''
                }`}
              >
                <div className="text-sm font-normal text-grey">Fine</div>
                <div className="flex items-center justify-center text-sm font-normal text-errorLite5">
                  <CurrencyAmount
                    value={Number(fineAmount)}
                    className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                    symbolClass={`${
                      isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                    }`}
                    valueClass=""
                  />
                </div>
              </div>
            )}

            {(Number(gstInfo?.cgstAmount) > 0 ||
              Number(gstInfo?.sgstAmount) > 0 ||
              Number(gstInfo?.igstAmount) > 0) && (
              <>
                <div className="flex cursor-pointer flex-col gap-3 border-t border-cofeeGreyBlue5 pt-3">
                  {Number(gstInfo?.cgstAmount) > 0 && (
                    <div className="flex justify-between">
                      <div className="text-sm font-normal text-grey">
                        {gstLineLabel('CGST', gstInfo?.cgstPercent)}
                      </div>
                      <div className="flex items-center justify-center text-sm font-normal ">
                        <CurrencyAmount
                          value={Number(gstInfo?.cgstAmount)}
                          className={`${
                            isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                          }`}
                          symbolClass={`${
                            isDefaultCurrency
                              ? 'text-[0.95em] !font-normal'
                              : ''
                          }`}
                          valueClass=""
                        />
                      </div>
                    </div>
                  )}
                  {Number(gstInfo?.sgstAmount) > 0 && (
                    <div className="flex justify-between">
                      <div className="text-sm font-normal text-grey">
                        {gstLineLabel('SGST', gstInfo?.sgstPercent)}
                      </div>
                      <div className="flex items-center justify-center text-sm font-normal ">
                        <CurrencyAmount
                          value={Number(gstInfo?.sgstAmount)}
                          className={`${
                            isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                          }`}
                          symbolClass={`${
                            isDefaultCurrency
                              ? 'text-[0.95em] !font-normal'
                              : ''
                          }`}
                          valueClass=""
                        />
                      </div>
                    </div>
                  )}
                  {Number(gstInfo?.igstAmount) > 0 && (
                    <div className="flex justify-between">
                      <div className="text-sm font-normal text-grey">
                        {gstLineLabel('IGST', gstInfo?.igstPercent)}
                      </div>
                      <div className="flex items-center justify-center text-sm font-normal ">
                        <CurrencyAmount
                          value={Number(gstInfo?.igstAmount)}
                          className={`${
                            isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                          }`}
                          symbolClass={`${
                            isDefaultCurrency
                              ? 'text-[0.95em] !font-normal'
                              : ''
                          }`}
                          valueClass=""
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex cursor-pointer justify-between border-t border-cofeeGreyBlue5 pt-3 text-sm font-normal ">
                  <div className="text-grey">Total GST</div>
                  <div className="flex items-center justify-center text-sm font-normal ">
                    <CurrencyAmount
                      value={Number(gstInfo?.totalGst)}
                      className={`${
                        isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                      }`}
                      symbolClass={`${
                        isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                      }`}
                      valueClass=""
                    />
                  </div>
                </div>
              </>
            )}
            {discountAmount && parseFloat(discountAmount) > 0 && (
              <div className="mt-3 flex cursor-pointer justify-between border-t border-cofeeGreyBlue5 pt-3">
                <div className="text-sm font-normal text-grey">
                  Discount Amount
                </div>
                <div className="flex items-center justify-center text-sm font-normal text-content">
                  -
                  <CurrencyAmount
                    value={Number(discountAmount)}
                    className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                    symbolClass={`${
                      isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                    }`}
                    valueClass=""
                  />
                </div>
              </div>
            )}
            {closedAmount && parseFloat(closedAmount) > 0 && (
              <div className={`flex cursor-pointer justify-between pt-3 `}>
                <div className="text-sm font-normal text-grey">
                  Closed Amount
                </div>
                <div className="flex items-center justify-center text-sm font-normal ">
                  -
                  <CurrencyAmount
                    value={Number(closedAmount)}
                    className={`${isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'}`}
                    symbolClass={`${
                      isDefaultCurrency ? 'text-[0.95em] !font-normal' : ''
                    }`}
                    valueClass=""
                  />
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            className="block w-full rounded-b-xl border-0 border-t border-cofeeGreyBlue5 bg-white p-0 text-left"
            onClick={handleToggleExpand}
          >
            <div className="px-4 pt-6">
              <TotalAmountFooter
                isDefaultCurrency={isDefaultCurrency}
                totalAmount={totalAmount}
                openAccordion={openAccordion}
                showChevron
              />
            </div>
          </button>
        </>
      ) : expandable ? (
        <button
          type="button"
          className="font-inherit block w-full cursor-pointer border-0 bg-transparent px-4 py-0 text-left"
          onClick={handleToggleExpand}
        >
          <TotalAmountFooter
            isDefaultCurrency={isDefaultCurrency}
            totalAmount={totalAmount}
            openAccordion={openAccordion}
            showChevron
          />
        </button>
      ) : (
        <div className="w-full px-4">
          <TotalAmountFooter
            isDefaultCurrency={isDefaultCurrency}
            totalAmount={totalAmount}
            openAccordion={openAccordion}
            showChevron={false}
          />
        </div>
      )}
    </div>
  );
};

export default FeeConfigAccordion;
